<script setup lang="ts">
import type { DetailNetAssociation } from "~/utils/netAssociationEstime";

interface CampaignInfo {
  organization: string;
  title: string;
  goalAmount: number | null;
  currentAmount: number;
  donorCount: number;
}

const props = defineProps<{
  organizationLogoUrl: string | null;
  campaignInfo: CampaignInfo;
  isDev: boolean;
  formatAmount: (amount: number) => string;
  formatAmountShort: (amount: number) => string;
  finalAmount: number;
  /**
   * Ce que l'association reçoit (`net`) : le don, MOINS les frais quand le
   * donateur ne les couvre pas, et le détail de ce qui est retiré
   * (utils/netAssociationEstime.ts).
   */
  detailAssociation: DetailNetAssociation;
  prestataire?: "stripe" | "paypal";
  finalAmountWithDiscount: number;
  campaignImageUrl: string | null;
  progressPercentage: number;
  primaryColor: string | null;
  includeFees: boolean;
  feesAmount: number;
  donateUs: boolean;
  donateToUsAmount: number;
  askMessage: boolean;
  message: string;
  /** Zone de message dépliée (même geste que /pay : un lien discret l'ouvre). */
  showMessage: boolean;
  /** Barre de temps (date de fin posée) : instant de clôture + jour en clair. */
  closesAt?: string | null;
  endDateLabel?: string | null;
  daysRemaining?: number | null;
  startsAt?: string | null;
}>();

const emit = defineEmits<{
  (e: "update:message", value: string): void;
  (e: "update:showMessage", value: boolean): void;
  /** La barre de temps est arrivée à zéro : la cagnotte vient de se clôturer. */
  (e: "ended"): void;
}>();

/** Dernières frappes : « 240 / 280 » n'apparaît qu'en approchant de la limite. */
const MESSAGE_MAX = 280;
const messageLength = computed(() => props.message.length);

const { t } = useI18n({ useScope: "local" });
</script>

<template>
  <!-- Desktop Campaign Info -->
  <div class="hidden lg:block space-y-6">
    <!-- Badge organisation -->
    <div class="flex items-center mb-4">
      <div
        class="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700 max-w-[250px]"
      >
        <img
          v-if="organizationLogoUrl"
          :src="organizationLogoUrl"
          :alt="campaignInfo.organization"
          class="w-4 h-4 mr-2 object-contain rounded-full shrink-0"
        />
        <UIcon
          v-else
          name="i-lucide-home"
          class="w-4 h-4 mr-2 shrink-0"
        />
        <span class="truncate">{{ campaignInfo.organization }}</span>
      </div>
      <div
        v-if="isDev"
        class="ml-2 px-3 py-1 bg-indigo-600 text-white rounded-full text-sm"
      >
        {{ t('badge.environnementTest') }}
      </div>
    </div>

    <!-- Titre principal : "Participer à la cagnotte <Nom de la cagnotte>" -->
    <h1 class="text-2xl font-semibold text-gray-900 mb-2">
      {{ t('campagne.paiementDesktop.participerTitre') }}
      <span v-if="campaignInfo.title" class="text-gray-900">{{ campaignInfo.title }}</span>
    </h1>
    <div class="text-3xl font-bold text-gray-900 mb-6">
      {{ formatAmount(finalAmountWithDiscount) }}
    </div>

    <!-- Détails du don -->
    <div class="space-y-4 mb-6">
      <div class="flex justify-between items-center py-2">
        <span class="text-gray-700">
          {{ t('campagne.paiementDesktop.organisationRecoit', { organisation: campaignInfo.organization }) }}
        </span>
        <span class="font-medium text-gray-900">
          {{ formatAmount(detailAssociation.net) }}
        </span>
      </div>
      <DonateDeductionsAssociation
        :detail="detailAssociation"
        :prestataire="prestataire"
        :format-amount="formatAmount"
      />

      <!-- Image de la campagne -->
      <div
        v-if="campaignImageUrl"
        class="rounded-xl overflow-hidden shadow-lg"
      >
        <img
          :src="campaignImageUrl"
          :alt="campaignInfo.title"
          class="w-full h-40 object-cover"
        />
      </div>

      <!-- Progression de la campagne -->
      <div class="bg-gray-50 rounded-xl p-4">
        <div :class="['flex justify-between items-end', campaignInfo.goalAmount ? 'mb-2' : '']">
          <div>
            <span class="text-xl font-bold text-gray-900">
              {{ formatAmountShort(campaignInfo.currentAmount) }}
            </span>
            <template v-if="campaignInfo.goalAmount">
              <span class="text-gray-500 text-sm ml-1">
                {{ t('campagne.paiementDesktop.surMontant', { montant: formatAmountShort(campaignInfo.goalAmount) }) }}
              </span>
            </template>
            <template v-else>
              <span class="text-gray-500 text-sm ml-1">
                {{ t('campagne.paiementDesktop.recolte', campaignInfo.currentAmount) }}
              </span>
            </template>
          </div>
          <div class="text-gray-500 text-sm">
            {{ t('campagne.paiementDesktop.participants', campaignInfo.donorCount) }}
          </div>
        </div>
        <div
          v-if="campaignInfo.goalAmount"
          class="w-full h-2 bg-gray-200 rounded-full overflow-hidden"
        >
          <!-- La couleur des boutons de montant et du bouton de don
               (`--payment-primary` : celle choisie par l'association, sinon
               le bleu de la page) — Karim 2026-09-30. -->
          <div
            class="h-full rounded-full transition-all duration-500"
            :style="{ width: `${progressPercentage}%`, background: 'var(--payment-primary)' }"
          />
        </div>
        <!-- Barre de temps : même règle que la page de la cagnotte. -->
        <CampaignCountdown
          v-if="closesAt && endDateLabel"
          :closes-at="closesAt"
          :starts-at="startsAt"
          :end-date-label="endDateLabel"
          :days-remaining="daysRemaining"
          compact
          class="mt-4"
          @ended="emit('ended')"
        />
      </div>

      <!-- Juste sous la progression, toujours visible en haut (encart
           réduction d'impôt — Karim 2026-10-22). -->
      <slot name="apres-progression" />
    </div>

    <!-- Sous-total et total -->
    <div class="border-t border-gray-200 pt-4">
      <div class="flex justify-between items-center py-2">
        <span class="text-gray-700">{{ t('campagne.paiementDesktop.sousTotal') }}</span>
        <span class="font-medium text-gray-900">
          {{ formatAmount(finalAmount) }}
        </span>
      </div>

      <!-- Frais si inclus -->
      <div
        v-if="includeFees && feesAmount > 0"
        class="flex justify-between items-center py-2 text-sm"
      >
        <span class="text-gray-500">{{ t('campagne.paiementDesktop.fraisCouverts') }}</span>
        <span class="text-gray-500">
          + {{ formatAmount(feesAmount) }}
        </span>
      </div>

      <!-- Don à Donoor si activé -->
      <div
        v-if="donateUs && donateToUsAmount > 0"
        class="flex justify-between items-center py-2 text-sm"
      >
        <span class="text-gray-500">{{ t('campagne.paiementDesktop.donADonoor') }}</span>
        <span class="text-gray-500">
          + {{ formatAmount(donateToUsAmount) }}
        </span>
      </div>

      <!-- Message du donateur (bureau) — même geste que /pay : un lien
           discret, puis la zone de saisie, rien de plus (Karim 2026-09-30 :
           le cadre dans le cadre était « à revoir »). -->
      <div v-if="askMessage && !showMessage" class="mt-3">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 py-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          @click="emit('update:showMessage', true)"
        >
          <UIcon name="i-lucide-message-circle" class="size-4" />
          <i18n-t keypath="campagne.paiementDesktop.ajouterMessage" tag="span">
            <template #optionnel><span class="text-gray-400">{{ t('campagne.paiementDesktop.optionnelLabel') }}</span></template>
          </i18n-t>
        </button>
      </div>
      <div v-if="askMessage && showMessage" class="mt-3">
        <div class="flex items-center justify-between mb-1.5">
          <label for="message-donateur" class="text-sm font-medium text-gray-700">
            {{ t('campagne.paiementDesktop.votreMessage') }}
          </label>
          <button
            type="button"
            class="text-xs text-gray-500 hover:text-gray-800 transition-colors"
            @click="
              emit('update:showMessage', false);
              emit('update:message', '');
            "
          >
            {{ t('commun.actions.supprimer') }}
          </button>
        </div>
        <textarea
          id="message-donateur"
          :value="message"
          :maxlength="MESSAGE_MAX"
          :placeholder="t('campagne.paiementDesktop.placeholderMessage')"
          rows="3"
          class="w-full px-3.5 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:bg-white focus:border-gray-400 resize-none transition-colors"
          @input="emit('update:message', ($event.target as HTMLTextAreaElement).value)"
        />
        <p
          v-if="messageLength >= MESSAGE_MAX - 40"
          class="mt-1 text-right text-xs text-gray-400 tabular-nums"
        >
          {{ messageLength }} / {{ MESSAGE_MAX }}
        </p>
      </div>

      <div
        class="flex justify-between items-center py-3 border-t border-gray-200 mt-4"
      >
        <span class="text-lg font-semibold text-gray-900">
          {{ t('campagne.paiementDesktop.montantTotalDu') }}
        </span>
        <span class="text-lg font-bold text-gray-900">
          {{ formatAmount(finalAmountWithDiscount) }}
        </span>
      </div>
    </div>
  </div>
</template>

<i18n lang="json" src="../../../i18n/pages/components/campaign-payment-page/CampaignPaymentDesktopSummary.json"></i18n>
